/**
 * Agent flight sidebar payable totals after extra commission (עמלה נוספת).
 *
 * Packaged RT/OW: SSOT is BookingFlowContext.pricing from the LDS pipeline
 * (computeLdsFlightPricing → applyLdsHandlingFeeAndCardToFareDetails).
 * Those ILS fields already include handleFee. Frozen offer
 * `priceTrace.finalClientUsdPerPerson` does not — do not use it as סכום לתשלום.
 *
 * Self-compose (הרכבה ידנית): when `selfComposeClientPay.perPaxUsd` is present,
 * that client-pay figure (HR max(% , $30) per direction) is the SSOT. Do not
 * let leftover LDS one-itinerary totals ($30 once) replace it.
 *
 * USD companions are ILS / usdRate (deal or live). Never multiply ILS by
 * catalogue FX (`convertPrice`) — that inflates ₪4,130 into ₪12,617.
 */

function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function firstFinitePositive(...candidates) {
  for (const candidate of candidates) {
    const n = finiteNumber(candidate);
    if (n != null && n > 0) return n;
  }
  return null;
}

function firstFiniteNonNegative(...candidates) {
  for (const candidate of candidates) {
    const n = finiteNumber(candidate);
    if (n != null && n >= 0) return n;
  }
  return 0;
}

function resolveHandleFeeIlsFromInput(handleFeeData, baseIls) {
  const type = handleFeeData?.type;
  const raw = finiteNumber(handleFeeData?.value);
  if (raw == null || raw <= 0) return 0;
  if (type === "%") {
    if (baseIls == null || !(baseIls > 0)) return 0;
    return (baseIls * raw) / 100;
  }
  if (type === "+" || type == null) return raw;
  return 0;
}

function toUsd(ils, usdRate) {
  const amount = finiteNumber(ils);
  const rate = finiteNumber(usdRate);
  if (amount == null || rate == null || rate <= 0) return null;
  return amount / rate;
}

function readSelfComposeClientPerPaxUsd(selfComposeClientPay) {
  const n = finiteNumber(selfComposeClientPay?.perPaxUsd);
  return n != null && n > 0 ? n : null;
}

/**
 * Card-aligned USD: Math.round(engine per-pax), then × pax.
 * ILS: ceil(offer per-pax ILS) × pax, else displayed USD × FX.
 */
function resolveSelfComposeClientPayTotals({
  selfComposeClientPay,
  handleFeeData,
  usdRate,
  paxN,
  extrasIlsFromCaller,
}) {
  const enginePerPaxUsd = readSelfComposeClientPerPaxUsd(selfComposeClientPay);
  if (enginePerPaxUsd == null) return null;

  const displayedPerPaxUsd = Math.round(enginePerPaxUsd);
  const baseUsd = displayedPerPaxUsd * paxN;
  const rate = finiteNumber(usdRate);
  const safeRate = rate != null && rate > 0 ? rate : null;
  const offerPerPaxIls = firstFinitePositive(selfComposeClientPay?.perPaxIls);
  const baseIls =
    offerPerPaxIls != null
      ? Math.ceil(offerPerPaxIls - 1e-9) * paxN
      : safeRate != null
        ? displayedPerPaxUsd * paxN * safeRate
        : null;
  // Ignore leftover LDS pipeline handleFee — it was computed on the wrong base.
  const handleFeeIls = resolveHandleFeeIlsFromInput(handleFeeData, baseIls);
  const extrasIls = firstFiniteNonNegative(
    extrasIlsFromCaller,
    selfComposeClientPay?.extrasIls
  );
  const handleFeeUsd =
    handleFeeIls <= 0 ? 0 : safeRate != null ? handleFeeIls / safeRate : null;
  const extrasUsd = toUsd(extrasIls, safeRate);
  const totalIls = baseIls != null ? baseIls + extrasIls + handleFeeIls : null;
  const totalUsd =
    Number.isFinite(baseUsd)
      ? baseUsd + (extrasUsd || 0) + (Number.isFinite(handleFeeUsd) ? handleFeeUsd : 0)
      : null;

  return {
    pax: paxN,
    usdRate: safeRate,
    source: "selfComposeClient",
    baseIls,
    baseUsd,
    handleFeeIls,
    handleFeeUsd,
    extrasIls,
    extrasUsd,
    totalIls,
    totalUsd,
    perPaxUsd: displayedPerPaxUsd,
    perPaxIls: baseIls != null ? baseIls / paxN : null,
    includesExtraCommission: handleFeeIls > 0.009,
  };
}

/**
 * @param {object} p
 * @param {object} [p.pricing] — BookingFlowContext.pricing
 * @param {object} [p.handleFeeData] — { type: '+'|'%', value: number } ILS or percent
 * @param {number} [p.usdRate]
 * @param {number} [p.pax=1]
 * @param {{ perPaxUsd?: number, perPaxIls?: number, extrasIls?: number }|null} [p.selfComposeClientPay]
 * @returns {{
 *   pax: number,
 *   usdRate: number|null,
 *   source: string,
 *   baseIls: number|null,
 *   baseUsd: number|null,
 *   handleFeeIls: number,
 *   handleFeeUsd: number|null,
 *   extrasIls: number,
 *   extrasUsd: number|null,
 *   totalIls: number|null,
 *   totalUsd: number|null,
 *   perPaxUsd: number|null,
 *   perPaxIls: number|null,
 *   includesExtraCommission: boolean,
 * }}
 */
export function resolveFlightAgentOrderSummaryTotals({
  pricing,
  handleFeeData,
  usdRate,
  pax = 1,
  selfComposeClientPay = null,
} = {}) {
  const paxN = Math.max(1, Math.floor(Number(pax) || 1));
  const rate = firstFinitePositive(usdRate, pricing?.managedOurTotalFare?.fx_rate);
  const selfComposeTotals = resolveSelfComposeClientPayTotals({
    selfComposeClientPay,
    handleFeeData,
    usdRate: rate,
    paxN,
    extrasIlsFromCaller: selfComposeClientPay?.extrasIls,
  });
  if (selfComposeTotals) return selfComposeTotals;

  const managed = pricing?.managedOurTotalFare || null;
  const source = String(pricing?.source || "");
  const currency = String(pricing?.currency || "").trim().toUpperCase();
  const ilsAuthoritative =
    source === "lds" ||
    managed != null ||
    currency === "ILS" ||
    currency === "₪";

  const extrasIls = ilsAuthoritative ? firstFiniteNonNegative(pricing?.extras) : 0;
  const baseIls = ilsAuthoritative
    ? firstFinitePositive(managed?.priceWithCommission, pricing?.base)
    : null;

  const handleFeeFromPipeline = firstFiniteNonNegative(
    managed?.handleFee,
    pricing?.handlingFee
  );
  const handleFeeFromInput = resolveHandleFeeIlsFromInput(handleFeeData, baseIls);
  const handleFeeIls = handleFeeFromPipeline > 0 ? handleFeeFromPipeline : handleFeeFromInput;

  if (!ilsAuthoritative) {
    return {
      pax: paxN,
      usdRate: rate,
      source: source || "fallback",
      baseIls: null,
      baseUsd: null,
      handleFeeIls,
      handleFeeUsd: toUsd(handleFeeIls, rate),
      extrasIls: 0,
      extrasUsd: null,
      totalIls: null,
      totalUsd: null,
      perPaxUsd: null,
      perPaxIls: null,
      includesExtraCommission: handleFeeIls > 0.009,
    };
  }

  const pipelineTotalIls = firstFinitePositive(
    managed?.priceWithCommissionAndCreditCardFee,
    managed?.priceWithHandleFee,
    pricing?.total
  );

  let totalIls = null;
  if (pipelineTotalIls != null && (managed != null || source === "lds")) {
    // LDS `pricing.total` already includes handleFee + extras.
    totalIls = firstFinitePositive(pricing?.total, pipelineTotalIls) ?? pipelineTotalIls;
  } else {
    const fallbackBase = firstFiniteNonNegative(pricing?.total, baseIls);
    totalIls = fallbackBase + handleFeeIls;
    if (!(totalIls > 0)) totalIls = null;
  }

  const baseUsd = toUsd(baseIls, rate);
  const handleFeeUsd = toUsd(handleFeeIls, rate);
  const extrasUsd = toUsd(extrasIls, rate);
  const totalUsd = toUsd(totalIls, rate);

  return {
    pax: paxN,
    usdRate: rate,
    source: ilsAuthoritative ? source || "lds" : source || "fallback",
    baseIls,
    baseUsd,
    handleFeeIls,
    handleFeeUsd,
    extrasIls,
    extrasUsd,
    totalIls,
    totalUsd,
    perPaxUsd: totalUsd != null ? totalUsd / paxN : null,
    perPaxIls: totalIls != null ? totalIls / paxN : null,
    includesExtraCommission: handleFeeIls > 0.009,
  };
}
